import { FormControl } from '../../../../../../widgets/formControl';
import { Icon } from '../../../../../../widgets/icon';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import Select from 'react-select';
import { T } from 'src/shared/helpers';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import styled from 'styled-components';
import { ComposerIconButton, ComposerToolbarButton, ComposerUnsupportedNote } from '../composerWorkspace';
import {
    ComposerAttributeNode,
    ComposerAttributeNodeLink,
    ComposerAttributeType,
    ComposerComparisonOperator,
    ComposerLogicalOperator
} from './composerAttributesModels';

const Rules = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const RuleHeader = styled.div`
    display: grid;
    grid-template-columns: minmax(100px, 1fr) 72px minmax(100px, 1fr) 28px;
    gap: 6px;
    color: ${(props) => props.theme.app.primary};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const RuleRow = styled.div`
    display: grid;
    grid-template-columns: minmax(100px, 1fr) 72px minmax(100px, 1fr) 28px;
    align-items: center;
    gap: 6px;
`;

const RuleEmpty = styled.div`
    padding: 14px;
    color: ${(props) => props.theme.app.primary};
    background: ${(props) => props.theme.app.pageBackgroundColor};
    border: 1px dashed ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    font-size: 12px;
    line-height: 18px;
`;

interface ComposerLinksEditorProps {
    attributes: ComposerAttributeNode[];
    links: ComposerAttributeNodeLink[];
    logicalOperator?: ComposerLogicalOperator;
    selfAttributeID?: string;
    subjectLabel: string;
    supported?: boolean;
    onChange: (links: ComposerAttributeNodeLink[], logicalOperator: ComposerLogicalOperator) => void;
}

const ComposerLinksEditor: FC<ComposerLinksEditorProps> = ({
    attributes,
    links,
    logicalOperator = ComposerLogicalOperator.AND,
    selfAttributeID,
    subjectLabel,
    supported = true,
    onChange
}) => {
    if (!supported) {
        return (
            <ComposerUnsupportedNote>
                {T._('Group and step constraints cannot be saved because the current product input has no constraint expression for these subjects.', 'Admin')}
            </ComposerUnsupportedNote>
        );
    }

    const availableAttributes = attributes.filter(
        (attribute) => attribute.type === ComposerAttributeType.Attribute && attribute.id !== selfAttributeID && attribute.options.length > 0
    );
    const attributeOptions = availableAttributes.map((attribute) => ({ value: attribute.id, label: attribute.name }));
    const logicalOptions = [
        { value: ComposerLogicalOperator.AND, label: T._('All of the following expressions are true', 'Admin') },
        { value: ComposerLogicalOperator.OR, label: T._('Any of the following expressions is true', 'Admin') }
    ];
    const comparisonOptions = [
        { value: ComposerComparisonOperator.EQUAL, label: T._('Equal', 'Admin') },
        { value: ComposerComparisonOperator.NOT_EQUAL, label: T._('Not Equal', 'Admin') }
    ];

    const updateLink = (index: number, nextLink: ComposerAttributeNodeLink) => {
        const nextLinks = links.map((link, linkIndex) => (linkIndex === index ? nextLink : link));
        onChange(nextLinks, logicalOperator);
    };

    const addLink = () => {
        const attribute = availableAttributes[0];
        const option = attribute?.options[0];
        if (!attribute || !option) return;
        onChange([...links, { attributeID: attribute.id, optionID: option.id, operation: ComposerComparisonOperator.EQUAL }], logicalOperator);
    };

    return (
        <Rules>
            <FormControl label={T._('Apply if', 'Admin')}>
                <Select
                    options={logicalOptions}
                    value={logicalOptions.find((option) => option.value === logicalOperator)}
                    onChange={(option) => onChange(links, option?.value ?? ComposerLogicalOperator.AND)}
                    menuPosition='fixed'
                />
            </FormControl>
            {links.length > 0 ? (
                <>
                    <RuleHeader aria-hidden='true'>
                        <span>{T._('Attribute', 'Admin')}</span>
                        <span>{T._('Operation', 'Admin')}</span>
                        <span>{T._('Option', 'Admin')}</span>
                        <span />
                    </RuleHeader>
                    {links.map((link, index) => {
                        const linkedAttribute = availableAttributes.find((attribute) => attribute.id === link.attributeID);
                        const optionOptions = (linkedAttribute?.options ?? []).map((option) => ({ value: option.id, label: option.name }));
                        return (
                            <RuleRow key={`${link.attributeID}_${link.optionID}_${index}`}>
                                <Select
                                    aria-label={T._('Attribute', 'Admin')}
                                    options={attributeOptions}
                                    value={attributeOptions.find((option) => option.value === link.attributeID) ?? null}
                                    onChange={(option) => {
                                        const attribute = availableAttributes.find((candidate) => candidate.id === option?.value);
                                        updateLink(index, {
                                            ...link,
                                            attributeID: attribute?.id ?? '',
                                            optionID: attribute?.options[0]?.id ?? ''
                                        });
                                    }}
                                    menuPosition='fixed'
                                />
                                <Select
                                    aria-label={T._('Operation', 'Admin')}
                                    options={comparisonOptions}
                                    value={comparisonOptions.find((option) => option.value === link.operation)}
                                    onChange={(option) => updateLink(index, { ...link, operation: option?.value ?? ComposerComparisonOperator.EQUAL })}
                                    menuPosition='fixed'
                                />
                                <Select
                                    aria-label={T._('Option', 'Admin')}
                                    options={optionOptions}
                                    value={optionOptions.find((option) => option.value === link.optionID) ?? null}
                                    onChange={(option) => updateLink(index, { ...link, optionID: option?.value ?? '' })}
                                    menuPosition='fixed'
                                />
                                <ZTooltip title={T._('Remove expression', 'Admin')}>
                                    <ComposerIconButton
                                        $danger
                                        aria-label={T._('Remove expression', 'Admin')}
                                        onClick={() =>
                                            onChange(
                                                links.filter((_, linkIndex) => linkIndex !== index),
                                                logicalOperator
                                            )
                                        }
                                    >
                                        <Icon>
                                            <DeleteIcon />
                                        </Icon>
                                    </ComposerIconButton>
                                </ZTooltip>
                            </RuleRow>
                        );
                    })}
                </>
            ) : (
                <RuleEmpty>{T._(`${subjectLabel} is always available. Add an expression to make it conditional.`, 'Admin')}</RuleEmpty>
            )}
            <ComposerToolbarButton disabled={availableAttributes.length === 0} onClick={addLink}>
                <PlusIcon />
                {T._('Add an expression', 'Admin')}
            </ComposerToolbarButton>
        </Rules>
    );
};

export default ComposerLinksEditor;
